<template>
  <div>
    <div class="ov-card">
      <div class="ov-card__head">
        <div class="ov-card__title">
          Permissions
          <span class="ov-count">{{ (permissions || []).length }}</span>
        </div>
        <div class="ov-filters">
          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input v-model="search" type="text" class="ov-input" :placeholder="$t('permission.searchPermission')" />
          </div>
          <button type="button" class="ov-btn ov-btn--primary ov-btn--sm" @click="openCreate">
            <v-icon small>mdi-plus</v-icon>
            {{ $t('permission.addPermission') }}
          </button>
        </div>
      </div>

      <div v-if="loadError" class="ov-card__body pl-error">
        <div class="ov-note ov-note--warning">
          <v-icon small color="#92400e">mdi-alert-outline</v-icon>
          <span>{{ loadError }}</span>
          <button type="button" class="pl-retry" @click="$emit('update-permissions')">Réessayer</button>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="permissions || []"
        :search="search"
        :loading="loading"
        loader-height="2"
        loading-text="Chargement des permissions..."
        no-data-text="Aucune permission enregistrée"
        no-results-text="Aucune permission ne correspond à la recherche"
        class="ov-table"
      >
        <template v-slot:[`item.code`]="{ item }">
          <span class="pl-code">{{ item.code }}</span>
        </template>
        <template v-slot:[`item.libelle`]="{ item }">
          <span class="pl-label-cell">{{ item.libelle }}</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="ov-row-actions pl-actions">
            <button type="button" class="ov-icon-btn" :title="$t('permission.editPermissionTitle')" @click="editPermission(item)">
              <v-icon small>mdi-pencil-outline</v-icon>
            </button>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- ===== PANNEAU AJOUT / MODIFICATION ===== -->
    <v-navigation-drawer v-model="drawerOpen" right temporary fixed width="420" class="ov-panel">
      <div class="ov-panel__head">
        <div>
          <div class="ov-card__kicker">{{ editedIndex === -1 ? 'Nouvelle permission' : 'Modification' }}</div>
          <div class="ov-card__title">{{ formTitle }}</div>
        </div>
        <button type="button" class="ov-icon-btn" title="Fermer" @click="closeDialogPermission()">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <form class="ov-panel__body" novalidate @submit.prevent="submit">
        <div class="ov-field">
          <label class="ov-label" for="pl-libelle">{{ $t('permission.permissionLabel') }}<span class="ov-required">*</span></label>
          <input
            id="pl-libelle"
            v-model.trim="editedItem.libelle"
            type="text"
            class="ov-input"
            :class="{ 'is-invalid': submitted && errors.libelle }"
            placeholder="Ex : Gérer les offres de voyage"
          />
          <div v-if="submitted && errors.libelle" class="ov-error">{{ errors.libelle }}</div>
        </div>

        <div v-if="editedIndex === -1" class="ov-field">
          <label class="ov-label" for="pl-code">{{ $t('permission.code') }}<span class="ov-required">*</span></label>
          <input
            id="pl-code"
            v-model.trim="editedItem.code"
            type="text"
            class="ov-input pl-code-input"
            :class="{ 'is-invalid': submitted && errors.code }"
            placeholder="Ex : PERM_CODE"
            autocomplete="off"
          />
          <div v-if="submitted && errors.code" class="ov-error">{{ errors.code }}</div>
          <div v-else class="ov-hint">Identifiant technique unique. Il ne pourra plus être modifié.</div>
        </div>

        <div v-else class="ov-stat">
          <span class="ov-stat__label">{{ $t('permission.code') }}</span>
          <span class="ov-stat__value pl-code">{{ editedItem.code }}</span>
        </div>

        <!-- Bouton caché : permet de valider avec Entrée -->
        <button type="submit" class="pl-hidden-submit" tabindex="-1" aria-hidden="true"></button>
      </form>

      <template #append>
        <div class="ov-panel__foot">
          <button type="button" class="ov-btn ov-btn--secondary" :disabled="overlay" @click="closeDialogPermission()">
            {{ $t('permission.cancel') }}
          </button>
          <button type="button" class="ov-btn ov-btn--primary" :disabled="overlay" @click="submit">
            <v-progress-circular v-if="overlay" indeterminate size="16" width="2"></v-progress-circular>
            <v-icon v-else small>mdi-content-save-outline</v-icon>
            {{ $t('permission.save') }}
          </button>
        </div>
      </template>
    </v-navigation-drawer>

    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
  </div>
</template>

<script>
import {
  API_CREER_FUNCTIONNALITY,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import {
  showErrorMessage,
  showSuccessMessage,
} from "@/modules/messages/messageProcess";
import axios from "axios";

export default {
  name: "PermissionList",

  props: {
    permissions: {
      type: Array,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadError: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      successMsg: null,
      errorMsg: null,
      overlay: false,
      search: "",
      submitted: false,

      drawerOpen: false,
      editedIndex: -1,

      editedItem: {
        code: null,
        libelle: null,
      },

      defaultItem: {
        code: null,
        libelle: null,
      },

      headers: [
        { text: this.$t('permission.reference'), value: "code" },
        { text: this.$t('permission.label'), value: "libelle" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1
        ? this.$t('permission.addPermissionTitle')
        : this.$t('permission.editPermissionTitle');
    },

    errors() {
      return {
        libelle: !this.editedItem.libelle ? "Le libellé est obligatoire." : null,
        code: this.editedIndex === -1 && !this.editedItem.code ? "Le code est obligatoire." : null,
      };
    },
  },

  watch: {
    // Fermer le panneau (clic à l'extérieur) réinitialise le formulaire
    drawerOpen(open) {
      if (!open) this.resetForm();
    },
  },

  methods: {
    openCreate() {
      this.resetForm();
      this.drawerOpen = true;
    },

    // SOUMISSION DU FORMULAIRE D'ENREGISTREMENT
    async submit() {
      if (this.overlay) return;
      this.submitted = true;
      if (this.errors.libelle || this.errors.code) return;
      if (this.editedIndex !== -1) {
        await this.persist("put");
      } else {
        await this.persist("post");
      }
    },

    // ENREGISTREMENT (POST) OU MODIFICATION (PUT) D'UNE PERMISSION.
    // Le panneau ne se ferme qu'en cas de succès : la saisie est conservée sinon.
    async persist(method) {
      this.overlay = true;
      const payload = { datas: [Object.assign({}, this.editedItem)] };
      try {
        const response = await axios[method](API_CREER_FUNCTIONNALITY, payload, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        });
        if (response.status == 200 && response.data.status.code == 800) {
          this.successMsg = response.data.status.message;
          showSuccessMessage();
          this.closeDialogPermission();
          this.$emit("update-permissions");
        } else {
          this.errorMsg = (response.data && response.data.status && response.data.status.message) || "Enregistrement impossible.";
          showErrorMessage();
        }
      } catch (e) {
        this.errorMsg = (e && e.message) || "Erreur réseau lors de l'enregistrement.";
        showErrorMessage();
      } finally {
        this.overlay = false;
      }
    },

    // EDITION D'UNE PERMISSION
    editPermission(item) {
      this.editedIndex = (this.permissions || []).indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.submitted = false;
      this.drawerOpen = true;
    },

    resetForm() {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
      this.submitted = false;
    },

    // FERMER LE PANNEAU DE CRÉATION / MODIFICATION
    closeDialogPermission() {
      this.drawerOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/admin/ui";

.pl-code {
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  font-size: 12px;
  font-weight: 700;
  color: $ov-navy;
  background: rgba(12, 36, 97, 0.06);
  padding: 3px 8px;
  border-radius: 6px;
  white-space: nowrap;
}

.pl-code-input {
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  letter-spacing: 0.02em;
}

.pl-label-cell {
  font-weight: 600;
  color: $ov-ink;
}

.pl-actions {
  justify-content: flex-end;
}

.pl-error {
  padding-bottom: 0;
}

.pl-retry {
  margin-left: auto;
  border: none;
  background: none;
  font-weight: 700;
  color: #92400e;
  text-decoration: underline;
  cursor: pointer;
}

.pl-hidden-submit {
  display: none;
}
</style>
